// src/lib/nativeApi.js
// Native-only backend-URL shim (FIX A for the Capacitor Android shell).
//
// The app calls its OWN backend everywhere via RELATIVE paths —
// `fetch('/.netlify/functions/...')`. In a browser those resolve against the
// page origin (app.tournyflex.com) and work. Inside the native shell the
// WebView origin is `https://localhost`, where there is NO backend, so those
// relative calls resolve to `https://localhost/.netlify/functions/...` and come
// back as empty/garbage responses served by the local asset layer — the
// post-login bootstrap never gets real data and the splash never dismisses.
//
// Fix: when (and ONLY when) running inside the native shell, wrap window.fetch
// so any relative app-backend path is rewritten to an ABSOLUTE production
// origin. On web nothing is installed — window.fetch is untouched, URLs stay
// relative, behavior is byte-identical.
//
// The URL-rewrite decision is the pure, exported `resolveApiUrl` so it can be
// unit-tested in node without a browser or Capacitor. The installer takes
// `isNative` as an argument (the caller passes `Capacitor.isNativePlatform()`)
// so this module has no Capacitor import and stays trivially importable in
// tests.

// Production app site (see CLAUDE.md — the App repo deploys to
// app.tournyflex.com). Overridable at BUILD time via VITE_NATIVE_API_ORIGIN so
// a dev/staging .aab can point elsewhere; the .aab bakes in whatever value was
// present when `npm run build` ran (import.meta.env is inlined at build time).
export const DEFAULT_NATIVE_API_ORIGIN = 'https://app.tournyflex.com'

// Pure decision: given a fetch URL, return the URL to actually request.
//   - not native            → return the URL UNCHANGED (web stays relative).
//   - native + relative
//     app-backend path       → prefix with the absolute production origin.
//   - native + already
//     absolute (http/https)  → return UNCHANGED (Supabase auth, Mapbox, any
//                              third-party absolute URL must never be rewritten).
//
// Only relative paths beginning with `/.netlify/` are rewritten — that is the
// sole shape the app uses to reach its own backend. Anything else (a relative
// asset path, an absolute URL, a non-string input) passes through untouched.
export function resolveApiUrl(inputUrl, { isNative = false, origin = DEFAULT_NATIVE_API_ORIGIN } = {}) {
  if (!isNative) return inputUrl
  if (typeof inputUrl !== 'string') return inputUrl
  if (inputUrl.startsWith('/.netlify/')) {
    return origin + inputUrl
  }
  return inputUrl
}

// Install the native-only fetch shim. On web (isNative === false) this installs
// NOTHING and returns false, guaranteeing byte-identical web behavior. On native
// it wraps window.fetch once, rewriting relative app-backend paths via
// resolveApiUrl. Returns true if the shim was installed.
//
// Idempotent: a second call is a no-op (guards against double-install).
export function installNativeApiShim({ isNative = false, origin } = {}) {
  if (!isNative) return false
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return false
  if (window.__tfNativeFetchInstalled) return false

  const resolvedOrigin =
    origin ||
    (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_NATIVE_API_ORIGIN) ||
    DEFAULT_NATIVE_API_ORIGIN

  const originalFetch = window.fetch.bind(window)
  window.fetch = function nativeFetch(input, init) {
    // Only string URLs are rewritten. A Request/URL object is always absolute
    // (or app-internal navigation) and is passed through unchanged — the app's
    // own backend calls always pass a string URL.
    if (typeof input === 'string') {
      return originalFetch(resolveApiUrl(input, { isNative: true, origin: resolvedOrigin }), init)
    }
    return originalFetch(input, init)
  }
  window.__tfNativeFetchInstalled = true
  return true
}
